// src/api/harClient.js
// Empty string = relative path → handled by CF Pages Function proxy in production
import { authHeader, handleAuthResponse, notifyAuthExpired } from "./authFetch";
import { downloadSavedHar } from "./downloadSavedHar";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api/har";

/** Returns Authorization header object if a token exists, else {}. Shared with
 *  client.js so both backend clients attach the token identically. */
function authHeaders() {
    return authHeader();
}

/** fetch wrapper: consume any rotated token (sliding session) and route a 401
 *  through the shared graceful re-auth flow. Callers keep their own !resp.ok
 *  handling for non-auth errors. */
async function hfetch(url, opts) {
    const resp = await fetch(url, opts);
    handleAuthResponse(resp);
    if (resp.status === 401) notifyAuthExpired();
    return resp;
}

export async function saveHarToServer(
    har,
    { title = "", favorite = false, labels = [], pageUrl = "" } = {}
) {
    const resp = await hfetch(`${BASE}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        // pageUrl only when known: the backend schema validates it as a URL, so an
        // empty string 400'd every launch evidence-HAR save ("Save failed: 400").
        body: JSON.stringify({ har, title, favorite, labels, ...(pageUrl ? { pageUrl } : {}) }),
    });
    if (!resp.ok) throw new Error(`Save failed: ${resp.status}`);
    return await resp.json(); // {id,...}
}

export async function listSavedHars({
                                        q = "",
                                        favorite = null,
                                        limit = 50,
                                        offset = 0,
                                        sort = "createdAt",
                                    } = {}) {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (favorite !== null) params.set("favorite", favorite ? "1" : "0");
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    params.set("sort", sort);
    const resp = await hfetch(`${BASE}?${params.toString()}`, {
        headers: { ...authHeaders() },
    });
    if (!resp.ok) throw new Error(`List failed: ${resp.status}`);
    return await resp.json();
}

export async function getHar(id) {
    const resp = await hfetch(`${BASE}/${encodeURIComponent(id)}`, {
        headers: { ...authHeaders() },
    });
    if (!resp.ok) throw new Error(`HAR load failed: ${resp.status}`);
    return await resp.json();
}

export async function getHarMeta(id) {
    const resp = await hfetch(`${BASE}/${encodeURIComponent(id)}/meta`, {
        headers: { ...authHeaders() },
    });
    if (!resp.ok) throw new Error(`Meta load failed: ${resp.status}`);
    return await resp.json();
}

export function downloadHar(id) {
    // window.open cannot carry the bearer header; fetch-to-blob instead.
    return downloadSavedHar(id, { apiBase: BASE.replace(/\/api\/har$/, "") });
}

export async function setFavorite(id, favorite) {
    const resp = await hfetch(`${BASE}/${encodeURIComponent(id)}/favorite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ favorite }),
    });
    if (!resp.ok) throw new Error(`Fav failed: ${resp.status}`);
    return await resp.json();
}

// Owner-only share switch (backend PATCH /api/har/:id/share, body { shared:boolean },
// response { id, shared }; 403 for a non-owner). A shared HAR is readable by every
// signed-in user, bodies included.
export async function setHarShared(id, shared) {
    const resp = await hfetch(`${BASE}/${encodeURIComponent(id)}/share`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ shared: !!shared }),
    });
    if (!resp.ok) throw new Error(resp.status === 403 ? "Only the owner can change sharing" : `Share failed: ${resp.status}`);
    return await resp.json(); // { id, shared }
}

export async function deleteHar(id) {
    const resp = await hfetch(`${BASE}/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: { ...authHeaders() },
    });
    if (!resp.ok) throw new Error(`Delete failed: ${resp.status}`);
    return await resp.json();
}
